import type { ImageCodec } from './codec';

/** AVIF 编码能力来源：浏览器原生 > WASM 兜底 > 不可用。 */
export type AvifEncoderKind = 'native' | 'wasm' | 'unavailable';

export const AVIF_MIME = 'image/avif';

export interface AvifEncoder {
  kind: AvifEncoderKind;
  /** kind === 'wasm' 时提供：把 RGBA 像素编码为 AVIF。 */
  encodeViaWasm?: (pixels: Uint8ClampedArray, width: number, height: number, quality: number) => Promise<Uint8Array>;
}

type JsquashEncode = (
  data: ImageData,
  options?: { quality?: number; lossless?: boolean; speed?: number },
) => Promise<ArrayBuffer>;

/** 懒加载 + 自检的 WASM 编码器；任何一步失败都视为不可用，绝不静默产出坏文件。 */
async function loadWasmEncoder(): Promise<AvifEncoder['encodeViaWasm'] | null> {
  try {
    const module = (await import('@jsquash/avif')) as { encode: JsquashEncode };
    const encode = module.encode;
    if (typeof encode !== 'function') return null;

    // 自检：编码 1×1 像素，确认 wasm 可实例化且产出非空。
    const probe = await encode(new ImageData(new Uint8ClampedArray([0, 0, 0, 255]), 1, 1), { quality: 50 });
    if (!probe || probe.byteLength === 0) return null;

    return async (pixels, width, height, quality) => {
      const buffer = await encode(new ImageData(pixels, width, height), { quality, speed: 6 });
      return new Uint8Array(buffer);
    };
  } catch {
    return null;
  }
}

export async function resolveAvifEncoder(codec: ImageCodec): Promise<AvifEncoder> {
  if (await codec.supportsEncoding(AVIF_MIME)) return { kind: 'native' };
  const encodeViaWasm = await loadWasmEncoder();
  if (encodeViaWasm) return { kind: 'wasm', encodeViaWasm };
  return { kind: 'unavailable' };
}
